import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./SearchComponent.scss";
import "./Adaptations.scss";
import loop from "../../static/img/header_img/search_loop.svg";
import { MainContext } from "../../contexts/MainContext";

export default function SearchComponent() {
    const [query, setQuery] = useState("");
    const [filteredResults, setFilteredResults] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isAnimating, setIsAnimating] = useState(false);
    const navigate = useNavigate();
    const ref = useRef(null);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    const { allCommunities, allProperties, allGroups } =
        useContext(MainContext);

    // Function to check if a community exists for a property
    const checkCommunityExists = (property) => {
        if (!property || !property.path) return false;
        
        // For a path like "/dubai/al-jaddaf/art-bay", we need to check if "/dubai/al-jaddaf" exists
        const pathParts = property.path.split('/');
        
        // Need at least 3 parts: ["", "dubai", "al-jaddaf", "art-bay"]
        if (pathParts.length < 4) return false;
        
        // Construct the community path: /dubai/al-jaddaf
        const communityPath = `/${pathParts[1]}/${pathParts[2]}`;
        
        // Find the community
        const community = allCommunities.find(community => 
            community.path === communityPath
        );
        
        // Check if community exists AND has a valid bird property
        return community && community.bird;
    };

    const communityGroupMarkersProperties = allGroups
        .map((element) => element.modal_map_coords_pin)
        .flat();

    // Include all properties, not just valid ones
    const allData = [
        ...allProperties.filter(property => property.enabled === undefined || property.enabled === true),
        ...allCommunities,
        ...communityGroupMarkersProperties.filter(property => property.enabled === undefined || property.enabled === true),
    ];

    const handleSearch = (e) => {
        e.preventDefault();
        if (highlightedIndex >= 0) {
            const selectedItem = filteredResults[highlightedIndex];
            // Check if it's a property with invalid community
            const isProperty = !selectedItem.city_path && selectedItem.path && selectedItem.path.split('/').length >= 4;
            
            if (isProperty && !checkCommunityExists(selectedItem)) {
                // Don't navigate for invalid properties
                return;
            }
            navigate(selectedItem.path);
        } else {
            const property = allData.find(
                (p) => p.name.toLowerCase() === query.toLowerCase()
            );
            if (property) {
                // Check if it's a property with invalid community
                const isProperty = !property.city_path && property.path && property.path.split('/').length >= 4;
                
                if (isProperty && !checkCommunityExists(property)) {
                    // Don't navigate for invalid properties
                    return;
                }
                navigate(property.path);
            }
        }
        setQuery("");
        setFilteredResults([]);
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        setQuery(inputValue);
        setHighlightedIndex(-1);

        if (inputValue.length > 0) {
            // const filtered = allData.filter((p) =>
            //     p.name
            //         .toLowerCase()
            //         .split(/\s+/)
            //         .some((word) => word.startsWith(inputValue.toLowerCase()))
            // );
            // const filtered = allData.filter((p) =>
            //     p.name.toLowerCase().includes(inputValue.toLowerCase())
            // );
            const filtered = allData.filter((p) => {
                const inputWords = inputValue.toLowerCase().split(/\s+/);
                const nameWords = p.name.toLowerCase().split(/\s+/);
            
                return inputWords.every(inputWord =>
                    nameWords.some(nameWord => nameWord.startsWith(inputWord))
                );
            });                     
            setFilteredResults(filtered);
        } else {
            setFilteredResults([]);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev < filteredResults.length - 1 ? prev + 1 : prev
            );

            // Scroll into view if needed
            if (suggestionsRef.current && highlightedIndex >= 0) {
                const highlightedElement =
                    suggestionsRef.current.children[highlightedIndex + 1];
                if (highlightedElement) {
                    highlightedElement.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                    });
                }
            }
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));

            // Scroll into view if needed
            if (suggestionsRef.current && highlightedIndex > 0) {
                const highlightedElement =
                    suggestionsRef.current.children[highlightedIndex - 1];
                if (highlightedElement) {
                    highlightedElement.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                    });
                }
            }
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0) {
                navigate(filteredResults[highlightedIndex].path);
                setFilteredResults([]);
                setQuery("");
            } else {
                handleSearch(e);
            }
        } else if (e.key === "Escape") {
            setIsAnimating(true);
            setTimeout(() => {
                setFilteredResults([]);
                setQuery("");
                setIsAnimating(false);
            }, 200);
        }
    };

    const handleClickOutside = (event) => {
        if (ref.current && !ref.current.contains(event.target)) {
            setIsAnimating(true);
            setTimeout(() => {
                setFilteredResults([]);
                setQuery("");
                setIsAnimating(false);
            }, 200);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref]);

    // Focus input on search button click
    const handleSearchBtnClick = (e) => {
        e.preventDefault();
        inputRef.current.focus();

        // Animate the search icon
        const loopElement = e.currentTarget.querySelector(".loop");
        if (loopElement) {
            loopElement.style.transform = "rotate(90deg)";
            setTimeout(() => {
                loopElement.style.transform = "rotate(0deg)";
            }, 300);
        }

        if (highlightedIndex >= 0) {
            const selectedItem = filteredResults[highlightedIndex];
            // Check if it's a property with invalid community
            const isProperty = !selectedItem.city_path && selectedItem.path && selectedItem.path.split('/').length >= 4;
            
            if (isProperty && !checkCommunityExists(selectedItem)) {
                // Don't navigate for invalid properties
                return;
            }
            navigate(selectedItem.path);
        } else if (filteredResults.length === 1) {
            const selectedItem = filteredResults[0];
            // Check if it's a property with invalid community
            const isProperty = !selectedItem.city_path && selectedItem.path && selectedItem.path.split('/').length >= 4;
            
            if (isProperty && !checkCommunityExists(selectedItem)) {
                // Don't navigate for invalid properties
                return;
            }
            navigate(selectedItem.path);
        } else {
            const property = allData.find(
                (p) => p.name.toLowerCase() === query.toLowerCase()
            );
            if (property) {
                // Check if it's a property with invalid community
                const isProperty = !property.city_path && property.path && property.path.split('/').length >= 4;
                
                if (isProperty && !checkCommunityExists(property)) {
                    // Don't navigate for invalid properties
                    return;
                }
                navigate(property.path);
            }
        }
        setQuery("");
        setFilteredResults([]);
    };

    return (
        <div className="search-container" ref={ref}>
            <form className="search-form" onSubmit={handleSearch}>
                <button
                    type="button"
                    className="search-btn"
                    onClick={handleSearchBtnClick}
                >
                    <img className="loop" src={loop} alt="search" />
                </button>
                <input
                    ref={inputRef}
                    placeholder="SEARCH"
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                />
            </form>
            {filteredResults.length > 0 && (
                <ul
                    ref={suggestionsRef}
                    className={`search-suggestions ${
                        isAnimating ? "animate-out" : ""
                    }`}
                    style={
                        isAnimating
                            ? { animation: "slideUp 0.2s ease-out forwards" }
                            : {}
                    }
                >
                    {filteredResults.map((item, index) => {
                        // Check if it's a property with invalid community
                        const isProperty = !item.city_path && item.path && item.path.split('/').length >= 4;
                        const isValid = !isProperty || checkCommunityExists(item);
                        
                        return (
                            <li
                                key={item.id}
                                onClick={() => {
                                    if (isValid) {
                                        navigate(item.path);
                                        setQuery("");
                                        setFilteredResults([]);
                                    }
                                }}
                                className={
                                    index === highlightedIndex ? "highlighted" : ""
                                }
                                style={{
                                    cursor: isValid ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {item.name}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
